// src/utils/authInterceptor.js
// Intercepteur global pour gérer les erreurs 401 (session expirée)
// AVEC auto-refresh du token avant redirection

import axios from 'axios';
import { supabase } from '../supabaseClient';

let isRedirecting = false; // Évite les redirections multiples
let isRefreshing = false; // Évite les refresh multiples simultanés
let refreshPromise = null; // Partage le refresh entre requêtes simultanées

/**
 * Tente de rafraîchir le token Supabase
 * @returns {Promise<string|null>} Le nouveau token ou null si échec
 */
const refreshToken = async () => {
  // Si un refresh est déjà en cours, attendre son résultat
  if (isRefreshing && refreshPromise) {
    return refreshPromise;
  }

  isRefreshing = true;
  
  refreshPromise = (async () => {
    try {
      console.log('🔄 Tentative de rafraîchissement du token...');
      
      const { data, error } = await supabase.auth.refreshSession();
      
      if (error) {
        console.error('❌ Échec refresh token:', error.message);
        return null;
      }
      
      if (data?.session?.access_token) {
        console.log('✅ Token rafraîchi avec succès');
        return data.session.access_token;
      }
      
      console.log('⚠️ Pas de nouveau token reçu');
      return null;
      
    } catch (err) {
      console.error('❌ Erreur refresh token:', err);
      return null;
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

/**
 * Configure l'intercepteur global fetch pour gérer les 401
 * Appelé une seule fois au démarrage de l'application
 */
export const setupAuthInterceptor = () => {
  const originalFetch = window.fetch;

  window.fetch = async (...args) => {
    try {
      const response = await originalFetch(...args);

      // Si 401 Unauthorized et pas déjà en cours de redirection
      if (response.status === 401 && !isRedirecting) {
        const url = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
        
        // Vérifier que c'est une requête vers notre API (pas une ressource externe)
        const isApiRequest = url.includes(process.env.REACT_APP_API_URL) || 
                            url.startsWith('/') ||
                            url.includes('firstarabic');

        if (isApiRequest) {
          console.log('🔒 Erreur 401 détectée - Tentative de refresh...');
          console.log('📍 URL:', url);
          
          // ========================================
          // 🔄 NOUVEAU: Tenter de rafraîchir le token
          // ========================================
          const newToken = await refreshToken();
          
          if (newToken) {
            console.log('🔄 Réessai de la requête avec nouveau token...');
            
            // Reconstruire la requête avec le nouveau token
            const [input, init = {}] = args;
            const newInit = {
              ...init,
              headers: {
                ...(init.headers || {}),
                'Authorization': `Bearer ${newToken}`
              }
            };
            
            // Réessayer la requête
            const retryResponse = await originalFetch(input, newInit);
            
            if (retryResponse.status === 401) {
              // Échec même après refresh → session vraiment expirée
              console.log('❌ Échec après refresh - Session définitivement expirée');
              await handleSessionExpired();
            } else {
              console.log('✅ Requête réussie après refresh');
              return retryResponse;
            }
          } else {
            // Impossible de rafraîchir → rediriger
            console.log('❌ Impossible de rafraîchir le token');
            await handleSessionExpired();
          }
        }
      }

      return response;
    } catch (error) {
      // Propager les erreurs réseau
      throw error;
    }
  };

  // ========================================
  // Intercepteur AXIOS (les appels axios ne passent PAS par window.fetch,
  // donc leurs 401 échappaient à la redirection → écran "Bonjour Professeur"
  // avec un jeton expiré). On applique le même comportement : refresh, sinon
  // déconnexion + redirection vers l'accueil.
  // ========================================
  axios.interceptors.response.use(
    (response) => response,
    async (error) => {
      const status = error?.response?.status;
      const cfg = error?.config || {};
      const url = cfg.url || '';
      const isApiRequest = url.includes(process.env.REACT_APP_API_URL) ||
                           url.startsWith('/') ||
                           url.includes('firstarabic');

      if (status === 401 && isApiRequest && !cfg._retry && !isRedirecting) {
        cfg._retry = true; // évite une boucle de retry
        const newToken = await refreshToken();
        if (newToken) {
          cfg.headers = { ...(cfg.headers || {}), Authorization: `Bearer ${newToken}` };
          return axios(cfg); // rejoue la requête avec le nouveau token
        }
        await handleSessionExpired(); // refresh impossible → redirection vers /
      }

      return Promise.reject(error);
    }
  );

  console.log('✅ Intercepteur d\'authentification configuré (fetch + axios, avec auto-refresh)');
};

/**
 * Gère l'expiration définitive de la session
 */
const handleSessionExpired = async () => {
  if (isRedirecting) return;
  
  isRedirecting = true;

  // Déconnecter l'utilisateur
  try {
    await supabase.auth.signOut();
    console.log('✅ Déconnexion Supabase effectuée');
  } catch (signOutError) {
    console.error('❌ Erreur déconnexion:', signOutError);
  }

  // Afficher un message à l'utilisateur
  alert('Votre session a expiré. Vous allez être redirigé vers la page de connexion.');

  // Rediriger vers la page d'accueil
  window.location.href = '/';
};

/**
 * Réinitialise les flags (utile pour les tests)
 */
export const resetRedirectFlag = () => {
  isRedirecting = false;
  isRefreshing = false;
  refreshPromise = null;
};

/**
 * Fonction utilitaire pour obtenir un token frais
 * À utiliser dans les composants qui font des appels API critiques
 */
export const getFreshToken = async () => {
  try {
    const { data: { session }, error } = await supabase.auth.getSession();
    
    if (error || !session) {
      // Pas de session, tenter un refresh
      const newToken = await refreshToken();
      return newToken;
    }
    
    // Vérifier si le token expire bientôt (dans moins de 5 minutes)
    const expiresAt = session.expires_at * 1000; // Convertir en ms
    const now = Date.now();
    const fiveMinutes = 5 * 60 * 1000;
    
    if (expiresAt - now < fiveMinutes) {
      console.log('⏰ Token expire bientôt, rafraîchissement proactif...');
      const newToken = await refreshToken();
      return newToken || session.access_token;
    }
    
    return session.access_token;
  } catch (err) {
    console.error('❌ Erreur getFreshToken:', err);
    return null;
  }
};

export default setupAuthInterceptor;
